import styled from '@emotion/styled'
import { Close as PopoverClose } from '@radix-ui/react-popover'
import { useMemo } from 'react'

import { EMPTY_FROZEN_ARRAY } from '@bemlo/utils'

import { ActionCardVariations } from './types'
import { Color, theme } from '../../constants'
import { useTranslations } from '../../translate'
import { ClickableIcon } from '../ClickableIcon'
import { Div } from '../Div'
import { Flex } from '../Flex'
import { IconName } from '../Icon'

const VerticalRule = styled(Div)(() => ({
  display: 'block',
  backgroundColor: Color.GREY,
  height: theme.spacing[3.5],
  width: 1,
}))

const Root = styled(Flex)({
  gridArea: 'Actions',
  alignItems: 'baseline',
  gap: theme.spacing[1.5],
  paddingTop: theme.spacing[3],
  paddingRight: theme.spacing[3],
  paddingBottom: theme.spacing[3],
})

export type ActionCardAction = {
  name: IconName
  onClick: () => void
  tooltipText?: string
  disabled?: boolean
  hidden?: boolean
}

export const Actions = ({
  icons,
  as,
  onClose,
}: {
  icons?: ActionCardAction[]
  as: ActionCardVariations
  onClose?: () => void
}) => {
  const t = useTranslations()

  const visibleIcons = useMemo(
    () => icons?.filter((icon) => !icon.hidden) ?? EMPTY_FROZEN_ARRAY,
    [icons],
  )

  if (!visibleIcons.length && !onClose && as !== 'popover') {
    return null
  }

  const closeClickableIcon = (
    <ClickableIcon
      onClick={onClose}
      tooltipText={t.common.close()}
      iconProps={{
        color: Color.DARKEST_MEDIUM_GREY,
        size: 'md',
        name: 'XIcon',
      }}
    />
  )

  const close =
    as === 'popover' ? (
      <PopoverClose asChild>{closeClickableIcon}</PopoverClose>
    ) : onClose ? (
      closeClickableIcon
    ) : null

  return (
    <Root>
      {visibleIcons.map(({ name, onClick, tooltipText, disabled }, index) => (
        <ClickableIcon
          key={index}
          onClick={onClick}
          iconProps={{
            color: Color.DARKEST_MEDIUM_GREY,
            size: 'md',
            name,
          }}
          tooltipText={tooltipText}
          disabled={disabled}
        />
      ))}

      {!!visibleIcons.length && close && <VerticalRule />}

      {close}
    </Root>
  )
}
